import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Input from "../../components/Input";
import { useAuth } from "../../context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [cid, setCid] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email"); // 'email' or 'cid'
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const loggedIn = await login(
        loginMethod === "email" ? email : cid,
        password,
        loginMethod
      );
      toast.success("Login successful!");
      const role = loggedIn?.user?.role || user?.role;

      const roleRedirects = {
        student: "/student/dashboard",
        tutor: "/tutor/dashboard",
        admin: "/admin/dashboard",
        default: "/"
      };

      navigate(roleRedirects[role] || roleRedirects.default);
    } catch (err) {
      const errorMsg = err.error || "Login failed. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-text">
            Sign in to your account
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-3">
            <div className="flex gap-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="loginMethod"
                  value="email"
                  checked={loginMethod === "email"}
                  onChange={(e) => {
                    setLoginMethod(e.target.value);
                    setCid("");
                    setError("");
                  }}
                  className="w-4 h-4"
                />
                <span className="ml-2 text-sm text-text">Email</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="loginMethod"
                  value="cid"
                  checked={loginMethod === "cid"}
                  onChange={(e) => {
                    setLoginMethod(e.target.value);
                    setEmail("");
                    setError("");
                  }}
                  className="w-4 h-4"
                />
                <span className="ml-2 text-sm text-text">College ID</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            {loginMethod === "email" ? (
              <Input
                label="Email address"
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            ) : (
              <Input
                label="College ID"
                type="text"
                id="cid"
                name="cid"
                autoComplete="username"
                required
                placeholder="Enter your College ID"
                value={cid}
                onChange={(e) => setCid(e.target.value)}
              />
            )}
            <Input
              label="Password"
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-text opacity-80"
              >
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary hover:opacity-80"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <Button type="submit" loading={loading} className="w-full">
            Sign in
          </Button>

          <div className="text-center">
            <Link
              to="/signup"
              className="text-sm text-primary hover:opacity-80"
            >
              Don't have an account? Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
